import * as React from 'react';

import DeliverableMediaInstagramPost from '../types/instagram-post/DeliverableMediaInstagramPost';
import DeliverableMediaInstagramStory from '../types/instagram-story/DeliverableMediaInstagramStory';
import DeliverableMediaYoutubeVideo from '../types/youtube-video/DeliverableMediaYoutubeVideo';
import DeliverableMediaOther from '../types/other/DeliverableMediaOther';
import DeliverableMediaBlog from '../types/blog/DeliverableMediaBlog';
import DeliverableMediaContentProductionForApproval from '../types/content-production/DeliverableMediaContentProductionForApproval';

import {
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO
} from 'utils/deliverables';

import type {
  Deliverable,
  DeliverableAttachment,
  DeliverableUploadedEventAction,
  Subcampaign
} from 'types';
import DeliverableMediaTikTokVideo
  from 'modules/deliverables/components/media/types/tiktok-video/DeliverableMediaTikTokVideo';

type Props = {
  deliverablesContainerRef: React.RefObject<any>,
  canModify: boolean,
  isForBrand: boolean,
  showValidation: boolean,
  subcampaign: Subcampaign,
  deliverable: Deliverable,
  onApprovalMode: () => void,
  onDeletePost: () => void,
  onDeleteAttachment: (a: DeliverableAttachment) => void,
  onDownloadAttachments: (a?: string | null) => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
};

type State = {
  canUpload: boolean
};

class DeliverableMedia extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const { canModify, isForBrand } = props;
    const isEnabledByDefault = canModify && !isForBrand;

    this.state = {
      canUpload: isEnabledByDefault
    };
  }

  handleUploadModeToggled = () => {
    this.setState(state => ({
      canUpload: !state.canUpload
    }));
  };

  handleTrackDeliverableUploadEvent = (eventAction: DeliverableUploadedEventAction): void => {
    const { onTrackDeliverableUploadEvent } = this.props;

    onTrackDeliverableUploadEvent(eventAction);
  };

  render(): React.ReactNode {
    const {
      deliverablesContainerRef,
      canModify,
      isForBrand,
      showValidation,
      subcampaign,
      deliverable,
      onApprovalMode,
      onDeletePost,
      onDeleteAttachment,
      onDownloadAttachments
    } = this.props;

    const { canUpload } = this.state;
    const onUploadModeToggled = (canModify && isForBrand) ? this.handleUploadModeToggled : undefined;

    switch (deliverable.deliverableType) {
      case DELIVERABLE_TYPE_INSTAGRAM_POST:
        return (
          <DeliverableMediaInstagramPost
            canUpload={canUpload}
            showValidation={showValidation}
            subcampaign={subcampaign}
            deliverable={deliverable}
            onDeletePost={onDeletePost}
            onDeleteAttachment={onDeleteAttachment}
            onDownloadAttachments={onDownloadAttachments}
            onUploadModeToggled={onUploadModeToggled}
            onTrackDeliverableUploadEvent={this.handleTrackDeliverableUploadEvent}
          />
        );
      case DELIVERABLE_TYPE_INSTAGRAM_STORY:
        return (
          <DeliverableMediaInstagramStory
            canUpload={canUpload}
            showValidation={showValidation}
            subcampaign={subcampaign}
            deliverable={deliverable}
            onDeleteAttachment={onDeleteAttachment}
            onDownloadAttachments={onDownloadAttachments}
            onUploadModeToggled={onUploadModeToggled}
            onTrackDeliverableUploadEvent={this.handleTrackDeliverableUploadEvent}
          />
        );
      case DELIVERABLE_TYPE_YOUTUBE_VIDEO:
        return (
          <DeliverableMediaYoutubeVideo
            canUpload={canUpload}
            showValidation={showValidation}
            subcampaign={subcampaign}
            deliverable={deliverable}
            onDeletePost={onDeletePost}
            onDownloadAttachments={onDownloadAttachments}
            onUploadModeToggled={onUploadModeToggled}
            onTrackDeliverableUploadEvent={this.handleTrackDeliverableUploadEvent}
          />
        );
      case DELIVERABLE_TYPE_OTHER:
        return (
          <DeliverableMediaOther
            canUpload={canUpload}
            subcampaign={subcampaign}
            deliverable={deliverable}
            onDeletePost={onDeletePost}
            onDeleteAttachment={onDeleteAttachment}
            onDownloadAttachments={onDownloadAttachments}
            onUploadModeToggled={onUploadModeToggled}
            onTrackDeliverableUploadEvent={this.handleTrackDeliverableUploadEvent}
          />
        );
      case DELIVERABLE_TYPE_BLOG:
        return (
          <DeliverableMediaBlog
            canUpload={canUpload}
            showValidation={showValidation}
            subcampaign={subcampaign}
            deliverable={deliverable}
            onDeletePost={onDeletePost}
            onDownloadAttachments={onDownloadAttachments}
            onUploadModeToggled={onUploadModeToggled}
            onTrackDeliverableUploadEvent={this.handleTrackDeliverableUploadEvent}
          />
        );
      case DELIVERABLE_TYPE_CONTENT_PRODUCTION:
        return (
          <DeliverableMediaContentProductionForApproval
            deliverablesContainerRef={deliverablesContainerRef}
            canApprove={isForBrand}
            canUpload={canUpload}
            subcampaign={subcampaign}
            deliverable={deliverable}
            onDownloadAttachments={onDownloadAttachments}
            onApprovalMode={onApprovalMode}
            onUploadModeToggled={onUploadModeToggled}
            onTrackDeliverableUploadEvent={this.handleTrackDeliverableUploadEvent}
          />
        );
      case DELIVERABLE_TYPE_TIKTOK_VIDEO:
        return (
          <DeliverableMediaTikTokVideo
            canUpload={canUpload}
            showValidation={showValidation}
            subcampaign={subcampaign}
            deliverable={deliverable}
            onDeletePost={onDeletePost}
            onDeleteAttachment={onDeleteAttachment}
            onDownloadAttachments={onDownloadAttachments}
            onUploadModeToggled={onUploadModeToggled}
            onTrackDeliverableUploadEvent={this.handleTrackDeliverableUploadEvent}
          />
        );
      default:
        break;
    }

    return null;
  }
}

export default DeliverableMedia;
